/**
 * Car Seat Adapter Compatibility Finder
 *
 * Guided selector for the Car Seat Adapter PDP. Resolves
 *   Series + Seats + Model  -> Size option value
 *   Brand                   -> Compatibility option value
 * and then drives the existing color-variant-selection radios so every other
 * piece of PDP machinery (price, gallery, URL, sticky bar, Ways to Buy,
 * Klaviyo BIS) reacts exactly as if the shopper had clicked the pickers.
 *
 * Markup / data: snippets/component-car-seat-adapter-finder.liquid
 * Docs:          .cursor/references/pdp-car-seat-adapter-finder.md
 */

if (!customElements.get('car-seat-adapter-finder')) {
  /**
   * @typedef {Object} FinderModel
   * @property {string} series   Series id (matches catalog.series[].id)
   * @property {number} seats
   * @property {string} label    Shown in the Model dropdown and breadcrumb
   * @property {string|null} size  Size option value, null = not compatible
   * @property {number} [capacity] Max adapters the wagon supports (default 1)
   */

  /**
   * @typedef {Object} FinderBrand
   * @property {string} name
   * @property {string[]} seats
   * @property {string} [note]
   */

  /**
   * Custom dropdown that enhances a native <select>.
   *
   * Native select popups are OS-level windows: on desktop they can overflow a
   * narrow browser window, and they ignore the Figma styling. This keeps the
   * <select> in the DOM as the single source of truth (value, options,
   * disabled) and renders an in-page button + role="listbox" that expands
   * inline below the trigger (same bordered box, pushes content down, chevron
   * rotates) exactly like the approved Figma prototype.
   *
   * Selecting an option sets select.value and dispatches a bubbling `change`,
   * so the finder's delegated onChange keeps working untouched. Programmatic
   * changes to the select (innerHTML / disabled / value + change) are picked
   * up through a MutationObserver + the `change` listener.
   */
  class FinderDropdown {
    static open = null;
    static MAX_HEIGHT = 280;

    /** @param {HTMLSelectElement} select */
    constructor(select) {
      this.select = select;
      this.wrapper = select.parentElement;
      this.activeIndex = -1;

      this.trigger = document.createElement('button');
      this.trigger.type = 'button';
      this.trigger.className = 'adapter-finder__select adapter-finder__dropdown-trigger';
      this.trigger.setAttribute('aria-haspopup', 'listbox');
      this.trigger.setAttribute('aria-expanded', 'false');
      const label = select.getAttribute('aria-label');
      if (label) this.trigger.setAttribute('aria-label', label);
      if (select.id) {
        this.trigger.id = `${select.id}-trigger`;
        // Re-point the visible <label> at the trigger so it stays the
        // accessible name and clicking it opens the dropdown.
        const forLabel = document.querySelector(`label[for="${select.id}"]`);
        if (forLabel) forLabel.htmlFor = this.trigger.id;
      }

      this.list = document.createElement('ul');
      this.list.className = 'adapter-finder__dropdown-list';
      this.list.setAttribute('role', 'listbox');
      this.list.tabIndex = -1;
      this.list.hidden = true;

      select.classList.add('adapter-finder__select--native');
      select.setAttribute('aria-hidden', 'true');
      select.tabIndex = -1;
      this.wrapper.classList.add('adapter-finder__select-wrapper--custom');
      this.wrapper.appendChild(this.trigger);
      this.wrapper.appendChild(this.list);

      this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);

      this.trigger.addEventListener('click', () => (this.isOpen ? this.close() : this.openList()));
      this.trigger.addEventListener('keydown', (e) => this.onTriggerKeydown(e));
      this.list.addEventListener('keydown', (e) => this.onListKeydown(e));
      this.list.addEventListener('click', (e) => {
        const option = e.target.closest('[role="option"]');
        if (option && !option.hasAttribute('aria-disabled')) this.choose(option.dataset.value);
      });
      this.list.addEventListener('mousemove', (e) => {
        const option = e.target.closest('[role="option"]');
        if (option) this.setActive(Array.from(this.list.children).indexOf(option), false);
      });
      select.addEventListener('change', () => this.sync());

      this.observer = new MutationObserver(() => this.sync());
      this.observer.observe(select, { childList: true, attributes: true, attributeFilter: ['disabled'] });

      this.sync();
    }

    get isOpen() {
      // Not `!list.hidden`: the list stays un-hidden while it collapses.
      return this.trigger.getAttribute('aria-expanded') === 'true';
    }

    /**
     * Rebuild the listbox from the select's options and mirror disabled/value.
     * The placeholder option ("Select", value "") is shown on the trigger only
     * – the Figma list contains just the real choices.
     */
    sync() {
      const { select } = this;
      this.list.innerHTML = '';
      Array.from(select.options).forEach((opt, index) => {
        if (opt.value === '') return;
        const li = document.createElement('li');
        li.className = 'adapter-finder__dropdown-option';
        li.setAttribute('role', 'option');
        li.id = `${this.trigger.id || 'adapter-finder-dropdown'}-opt-${index}`;
        li.dataset.value = opt.value;
        li.textContent = opt.text;
        li.setAttribute('aria-selected', String(opt.selected));
        this.list.appendChild(li);
      });

      const selected = select.options[select.selectedIndex];
      this.trigger.textContent = selected ? selected.text : '';
      this.trigger.classList.toggle('adapter-finder__dropdown-trigger--placeholder', !select.value);
      this.trigger.disabled = select.disabled;
      if (select.disabled && this.isOpen) this.close();
      else if (this.isOpen) this.setExpandedHeight();
    }

    /**
     * Figma prototype behaviour: the box grows in place (content below is
     * pushed down), options fade in and the chevron turns to point up. Height
     * is animated via max-height (0 → content height, capped at MAX_HEIGHT so
     * long lists scroll inside the box).
     */
    openList() {
      if (this.trigger.disabled) return;
      if (FinderDropdown.open && FinderDropdown.open !== this) FinderDropdown.open.close();
      FinderDropdown.open = this;
      this.cancelCloseTransition();

      this.list.hidden = false;
      this.list.style.maxHeight = '0px';
      this.trigger.setAttribute('aria-expanded', 'true');
      // Force a layout so the max-height change below actually transitions.
      void this.list.offsetHeight;
      this.wrapper.classList.add('adapter-finder__select-wrapper--open');
      this.setExpandedHeight();

      const options = Array.from(this.list.children);
      const selectedIndex = options.findIndex((li) => li.getAttribute('aria-selected') === 'true');
      this.setActive(Math.max(0, selectedIndex), false);
      this.list.focus({ preventScroll: true });

      // Once expanded, nudge the page so the whole list is on screen (the box
      // can extend past the bottom of a short viewport).
      this.onOpenEnd = (event) => {
        if (event.target !== this.list) return;
        this.list.removeEventListener('transitionend', this.onOpenEnd);
        this.onOpenEnd = null;
        if (!this.isOpen) return;
        const rect = this.list.getBoundingClientRect();
        if (rect.bottom > window.innerHeight) this.list.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        const active = options[this.activeIndex];
        if (active) active.scrollIntoView({ block: 'nearest' });
      };
      this.list.addEventListener('transitionend', this.onOpenEnd);

      document.addEventListener('pointerdown', this.onDocumentPointerDown, true);
    }

    setExpandedHeight() {
      this.list.style.maxHeight = `${Math.min(FinderDropdown.MAX_HEIGHT, this.list.scrollHeight)}px`;
    }

    close(returnFocus = false) {
      if (!this.isOpen) return;
      if (this.onOpenEnd) {
        this.list.removeEventListener('transitionend', this.onOpenEnd);
        this.onOpenEnd = null;
      }
      this.trigger.setAttribute('aria-expanded', 'false');
      this.wrapper.classList.remove('adapter-finder__select-wrapper--open');
      this.list.removeAttribute('aria-activedescendant');
      if (FinderDropdown.open === this) FinderDropdown.open = null;
      document.removeEventListener('pointerdown', this.onDocumentPointerDown, true);
      if (returnFocus) this.trigger.focus({ preventScroll: true });

      // Collapse, then hide once the height transition has finished.
      this.list.style.maxHeight = '0px';
      this.wrapper.classList.add('adapter-finder__select-wrapper--closing');
      this.onCloseEnd = (event) => {
        if (event && event.target !== this.list) return;
        this.cancelCloseTransition();
        this.list.hidden = true;
      };
      this.list.addEventListener('transitionend', this.onCloseEnd);
      // Safety net for browsers/users with transitions disabled.
      this.closeTimer = window.setTimeout(() => this.onCloseEnd && this.onCloseEnd(), 400);
    }

    cancelCloseTransition() {
      if (this.onCloseEnd) this.list.removeEventListener('transitionend', this.onCloseEnd);
      this.onCloseEnd = null;
      window.clearTimeout(this.closeTimer);
      this.wrapper.classList.remove('adapter-finder__select-wrapper--closing');
    }

    onDocumentPointerDown(event) {
      if (!this.wrapper.contains(event.target)) this.close();
    }

    /** @param {string} value */
    choose(value) {
      const changed = this.select.value !== value;
      this.select.value = value;
      this.close(true);
      if (changed) this.select.dispatchEvent(new Event('change', { bubbles: true }));
      else this.sync();
    }

    setActive(index, scroll) {
      const options = Array.from(this.list.children);
      if (!options.length) return;
      this.activeIndex = Math.min(Math.max(index, 0), options.length - 1);
      options.forEach((li, i) => li.classList.toggle('adapter-finder__dropdown-option--active', i === this.activeIndex));
      const active = options[this.activeIndex];
      this.list.setAttribute('aria-activedescendant', active.id);
      if (scroll) active.scrollIntoView({ block: 'nearest' });
    }

    onTriggerKeydown(event) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
        event.preventDefault();
        this.openList();
      }
    }

    onListKeydown(event) {
      const options = Array.from(this.list.children);
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.setActive(this.activeIndex + 1, true);
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.setActive(this.activeIndex - 1, true);
          break;
        case 'Home':
          event.preventDefault();
          this.setActive(0, true);
          break;
        case 'End':
          event.preventDefault();
          this.setActive(options.length - 1, true);
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          if (options[this.activeIndex]) this.choose(options[this.activeIndex].dataset.value);
          break;
        case 'Escape':
          event.preventDefault();
          this.close(true);
          break;
        case 'Tab':
          this.close();
          break;
        default:
          if (event.key.length === 1 && /\S/.test(event.key)) {
            // Type-ahead: jump to the next option starting with the typed letter.
            const key = event.key.toLowerCase();
            const start = this.activeIndex + 1;
            for (let i = 0; i < options.length; i++) {
              const li = options[(start + i) % options.length];
              if (li.textContent.trim().toLowerCase().startsWith(key)) {
                this.setActive((start + i) % options.length, true);
                break;
              }
            }
          }
      }
    }
  }

  customElements.define(
    'car-seat-adapter-finder',
    class CarSeatAdapterFinder extends HTMLElement {
      /** English fallbacks; the Liquid snippet passes translations in config.strings. */
      static DEFAULT_STRINGS = {
        select_placeholder: 'Select',
        choose_brand: 'Choose Brand',
        choose_car_seat: 'Choose Car Seat',
        all_brand_models: 'All [brand] models',
        seater: '[count] Seater',
        compatible: 'Compatible',
        not_compatible: 'Not Compatible',
        select_options: 'Select Options',
        add_to_cart: 'Add to Cart',
        capacity_one: '[model] supports up to [count] adapter.',
        capacity_other: '[model] supports up to [count] adapters.',
        incompatible_fallback: 'This car seat adapter is not compatible with your selection. Please check the Compatibility Guide.',
      };

      get LOCKED_LABEL() {
        return this.t('select_options');
      }
      get INCOMPATIBLE_LABEL() {
        return this.t('not_compatible');
      }
      get ATC_LABEL() {
        return this.t('add_to_cart');
      }

      /**
       * Translated string with `[name]` placeholders filled in.
       * @param {string} key
       * @param {Record<string, string|number>} [vars]
       */
      t(key, vars = {}) {
        const strings = (this.config && this.config.strings) || {};
        let text = strings[key] || CarSeatAdapterFinder.DEFAULT_STRINGS[key] || '';
        for (const [name, value] of Object.entries(vars)) text = text.split(`[${name}]`).join(String(value));
        return text;
      }

      config = null;
      state = { series: null, seats: null, model: null, brand: null, seat: null };
      /** @type {{ variant: Object|null, model: FinderModel|null, compatible: boolean }|null} */
      result = null;
      form = null;
      atcButton = null;
      ctaRow = null;
      /** Back button — lives in the CTA row OUTSIDE this element after buildCtaRow() */
      backButton = null;
      onExternalVariantChange = null;
      dispatching = false;
      _brandList = null;
      /** @type {FinderDropdown[]} */
      dropdowns = [];
      helpToggle = null;
      onHelpKeydown = null;
      onHelpOverlayClick = null;

      connectedCallback() {
        this.config = this.readConfig();
        if (!this.config) return;

        this.form = document.getElementById(this.dataset.productFormId) || this.closest('form');
        this.atcButton = this.form ? this.form.querySelector('#add-to-cart-btn') : null;

        this.buildCtaRow();
        this.bindNoticeToggle();
        this.renderSeries();
        this.renderSeats();
        this.renderModels();
        this.renderBrands();
        this.renderSeatModels();
        this.enhanceSelects();
        this.bindEvents();

        this.onExternalVariantChange = this.handleExternalVariantChange.bind(this);
        document.addEventListener('variant:change', this.onExternalVariantChange);
        this.setupStickyBar();

        const preselected = this.dataset.preselectedVariant;
        if (preselected && this.applyPreselectedVariant(preselected)) return;

        this.showStep('form');
        this.lockAtc(this.LOCKED_LABEL, true);
      }

      disconnectedCallback() {
        if (this.onExternalVariantChange) {
          document.removeEventListener('variant:change', this.onExternalVariantChange);
        }
        for (const dropdown of this.dropdowns) dropdown.close();
        this.closeModelHelp();
        this.teardownStickyBar();
      }

      /* ------------------------------------------------------------------ */
      /* Bottom sticky bar ("Find My Adapter")                               */
      /* ------------------------------------------------------------------ */

      /**
       * The bar (component-car-seat-adapter-finder-sticky-bar.liquid) sits
       * outside this element. Show it the moment the CTA row
       * (.adapter-finder__cta-row) has scrolled up out of view, i.e. its
       * bottom edge has passed under the fixed header, or the top of the
       * viewport when the header is hidden/not fixed.
       */
      setupStickyBar() {
        this.stickyBar = document.querySelector('[data-finder-sticky]');
        if (!this.stickyBar) return;

        this.onStickyClick = (event) => {
          event.preventDefault();
          this.scrollToFinder();
        };
        const button = this.stickyBar.querySelector('[data-finder-sticky-button]');
        if (button) button.addEventListener('click', this.onStickyClick);

        const header = document.getElementById('shopify-section-header');

        const update = () => {
          this.stickyFrame = null;
          // The row is display:none on the form step (lockAtc hide) and would
          // measure as 0; fall back to the finder, which ends where the row sits.
          const row = this.ctaRow || this.atcButton;
          const target = row && row.getClientRects().length ? row : this;
          // A fixed/sticky header covers the top strip; a hidden or scrolled-away
          // header reports a bottom <= 0, which clamps to the viewport top.
          const headerBottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
          this.toggleStickyBar(target.getBoundingClientRect().bottom <= headerBottom);
        };
        this.onStickyScroll = () => {
          if (!this.stickyFrame) this.stickyFrame = requestAnimationFrame(update);
        };
        window.addEventListener('scroll', this.onStickyScroll, { passive: true });
        window.addEventListener('resize', this.onStickyScroll, { passive: true });
        update();
      }

      teardownStickyBar() {
        if (this.stickyFrame) cancelAnimationFrame(this.stickyFrame);
        if (this.onStickyScroll) {
          window.removeEventListener('scroll', this.onStickyScroll);
          window.removeEventListener('resize', this.onStickyScroll);
        }
        if (this.stickyBar && this.onStickyClick) {
          const button = this.stickyBar.querySelector('[data-finder-sticky-button]');
          if (button) button.removeEventListener('click', this.onStickyClick);
        }
        this.toggleStickyBar(false);
      }

      /** @param {boolean} visible */
      toggleStickyBar(visible) {
        if (!this.stickyBar) return;
        this.stickyBar.classList.toggle('adapter-finder-sticky--visible', visible);
        this.stickyBar.setAttribute('aria-hidden', visible ? 'false' : 'true');
        // Lets other fixed bottom UI (scroll-to-top arrow) move out of the way.
        document.body.classList.toggle('adapter-finder-sticky-visible', visible);
        document.documentElement.style.setProperty('--adapter-finder-sticky-height', `${visible ? this.stickyBar.offsetHeight : 0}px`);
      }

      /** Replace native select popups with in-viewport custom listboxes. */
      enhanceSelects() {
        this.dropdowns = Array.from(this.querySelectorAll('select.adapter-finder__select')).map((select) => new FinderDropdown(select));
      }

      /* ------------------------------------------------------------------ */
      /* Config                                                              */
      /* ------------------------------------------------------------------ */

      readConfig() {
        const raw = this.querySelector('[data-finder-config]');
        if (!raw) return null;
        try {
          const parsed = JSON.parse(raw.textContent);
          parsed.catalog = parsed.catalog || {};
          parsed.catalog.series = Array.isArray(parsed.catalog.series) ? parsed.catalog.series : [];
          parsed.catalog.seats = Array.isArray(parsed.catalog.seats) ? parsed.catalog.seats : [2, 4, 6];
          parsed.catalog.models = Array.isArray(parsed.catalog.models) ? parsed.catalog.models : [];
          parsed.brands = Array.isArray(parsed.brands) ? parsed.brands : [];
          parsed.sizeValues = Array.isArray(parsed.sizeValues) ? parsed.sizeValues : [];
          parsed.compatValues = Array.isArray(parsed.compatValues) ? parsed.compatValues : [];
          parsed.strings = parsed.strings && typeof parsed.strings === 'object' ? parsed.strings : {};
          return parsed;
        } catch (error) {
          console.error('[car-seat-adapter-finder] invalid config JSON', error);
          return null;
        }
      }

      get models() {
        return this.config.catalog.models;
      }

      get variants() {
        return Array.isArray(window.productVariants) ? window.productVariants : [];
      }

      /**
       * Brand list in Compatibility option order. Grouped option values
       * ("Nuna | Cybex | Maxi-Cosi | Clek") become individual brands that all
       * resolve back to the same option value.
       * @returns {{ name: string, compatValue: string }[]}
       */
      get brandList() {
        if (this._brandList) return this._brandList;
        const list = [];
        for (const compatValue of this.config.compatValues) {
          for (const part of String(compatValue).split('|')) {
            const name = part.trim();
            if (name && !list.some((b) => b.name.toLowerCase() === name.toLowerCase())) {
              list.push({ name, compatValue });
            }
          }
        }
        this._brandList = list;
        return list;
      }

      /** @param {string} brandName @returns {string[]} */
      seatsForBrand(brandName) {
        const key = String(brandName).toLowerCase();
        const seats = [];
        for (const entry of this.config.brands) {
          if (String(entry.name || '').toLowerCase() !== key) continue;
          for (const seat of entry.seats || []) {
            const clean = String(seat).trim();
            if (clean && !seats.includes(clean)) seats.push(clean);
          }
        }
        return seats;
      }

      /** @param {string} brandName @returns {string} */
      noteForBrand(brandName) {
        const key = String(brandName).toLowerCase();
        const entry = this.config.brands.find((b) => String(b.name || '').toLowerCase() === key);
        return entry && entry.note ? String(entry.note).trim() : '';
      }

      /* ------------------------------------------------------------------ */
      /* Rendering                                                           */
      /* ------------------------------------------------------------------ */

      renderSeries() {
        const container = this.querySelector('[data-finder-series]');
        if (!container) return;
        container.innerHTML = '';
        for (const series of this.config.catalog.series) {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'adapter-finder__pill';
          btn.dataset.finderSeriesOption = series.id;
          btn.textContent = series.label;
          btn.setAttribute('aria-pressed', String(this.state.series === series.id));
          if (this.state.series === series.id) btn.classList.add('adapter-finder__pill--active');
          container.appendChild(btn);
        }
      }

      renderSeats() {
        const container = this.querySelector('[data-finder-seats]');
        if (!container) return;
        container.innerHTML = '';
        for (const seats of this.config.catalog.seats) {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'adapter-finder__pill';
          btn.dataset.finderSeatsOption = String(seats);
          btn.textContent = this.t('seater', { count: seats });
          const enabled =
            !this.state.series || this.models.some((m) => m.series === this.state.series && Number(m.seats) === Number(seats));
          btn.disabled = !enabled;
          const active = Number(this.state.seats) === Number(seats);
          btn.setAttribute('aria-pressed', String(active));
          if (active) btn.classList.add('adapter-finder__pill--active');
          container.appendChild(btn);
        }
      }

      renderModels() {
        const select = this.querySelector('[data-finder-model]');
        if (!select) return;
        const models = this.models.filter((m) => m.series === this.state.series && Number(m.seats) === Number(this.state.seats));
        select.innerHTML = '';
        select.appendChild(new Option(this.t('select_placeholder'), ''));
        for (const model of models) {
          select.appendChild(new Option(model.label, model.label));
        }
        select.disabled = models.length === 0;
        select.value = models.some((m) => m.label === this.state.model) ? this.state.model : '';
        if (!select.value) this.state.model = null;
      }

      renderBrands() {
        const select = this.querySelector('[data-finder-brand]');
        if (!select) return;
        select.innerHTML = '';
        select.appendChild(new Option(this.t('choose_brand'), ''));
        for (const brand of this.brandList) {
          select.appendChild(new Option(brand.name, brand.name));
        }
        select.value = this.state.brand || '';
      }

      renderSeatModels() {
        const select = this.querySelector('[data-finder-seat]');
        if (!select) return;
        const seats = this.state.brand ? this.seatsForBrand(this.state.brand) : [];
        select.innerHTML = '';
        select.appendChild(new Option(this.t('choose_car_seat'), ''));
        this.catchAllLabel = null;
        if (this.state.brand && seats.length === 0) {
          // No curated list for this brand: keep the flow moving with a
          // catch-all so Search can still resolve a variant.
          this.catchAllLabel = this.t('all_brand_models', { brand: this.state.brand });
          select.appendChild(new Option(this.catchAllLabel, this.catchAllLabel));
        }
        for (const seat of seats) {
          select.appendChild(new Option(seat, seat));
        }
        select.disabled = !this.state.brand;
        select.value = Array.from(select.options).some((o) => o.value === this.state.seat) ? this.state.seat : '';
        if (!select.value) this.state.seat = null;
      }

      renderBreadcrumb() {
        const list = this.querySelector('[data-finder-breadcrumb]');
        if (!list) return;
        const seriesLabel = this.seriesLabel(this.state.series);
        const parts = [
          seriesLabel,
          this.state.seats ? this.t('seater', { count: this.state.seats }) : '',
          this.state.model,
          this.seatCrumb(),
        ].filter(Boolean);
        list.innerHTML = '';
        for (const part of parts) {
          const li = document.createElement('li');
          li.className = 'adapter-finder__crumb';
          li.textContent = part;
          list.appendChild(li);
        }
      }

      seatCrumb() {
        if (!this.state.brand) return '';
        if (!this.state.seat || this.state.seat === this.catchAllLabel) return this.state.brand;
        const seat = this.state.seat;
        return seat.toLowerCase().startsWith(this.state.brand.toLowerCase()) ? seat : `${this.state.brand} ${seat}`;
      }

      seriesLabel(id) {
        const series = this.config.catalog.series.find((s) => s.id === id);
        return series ? series.label : '';
      }

      renderResult() {
        if (!this.result) return;
        const { variant, model, compatible } = this.result;
        const card = this.querySelector('[data-finder-result-card]');
        const statusText = this.querySelector('[data-finder-status-text]');
        const status = this.querySelector('[data-finder-status]');
        const thumb = this.querySelector('[data-finder-thumb]');
        const capacity = this.querySelector('[data-finder-capacity]');
        const qty = this.querySelector('[data-finder-qty]');

        this.renderBreadcrumb();

        if (card) card.classList.toggle('adapter-finder__result-card--incompatible', !compatible);
        if (status) status.classList.toggle('adapter-finder__status--incompatible', !compatible);
        if (statusText) statusText.textContent = compatible ? this.t('compatible') : this.t('not_compatible');

        if (thumb) {
          const src =
            variant && variant.featured_image && variant.featured_image.src ? variant.featured_image.src : this.dataset.productImage;
          if (src) thumb.src = src;
        }

        const max = Math.max(1, Number(model && model.capacity) || 1);
        if (qty) {
          qty.hidden = !compatible;
          const input = qty.querySelector('[data-finder-qty-input]');
          if (input) {
            input.max = String(max);
            input.value = String(Math.min(Math.max(1, Number(input.value) || 1), max));
          }
          this.syncQtyButtons();
        }

        if (capacity) {
          if (compatible && model) {
            capacity.textContent = this.t(max === 1 ? 'capacity_one' : 'capacity_other', { model: model.label, count: max });
            capacity.hidden = false;
          } else if (!compatible) {
            // Brand footnote only when the wagon itself is supported (size
            // present); for an unsupported wagon (catalog row with size null)
            // the car seat note would be misleading.
            const wagonSupported = !!(model && model.size);
            const note = wagonSupported && this.state.brand ? this.noteForBrand(this.state.brand) : '';
            capacity.textContent = note || this.t('incompatible_fallback');
            capacity.hidden = false;
          } else {
            capacity.hidden = true;
          }
        }
      }

      showStep(step) {
        for (const el of this.querySelectorAll('[data-finder-step]')) {
          el.hidden = el.dataset.finderStep !== step;
        }
        if (this.backButton) this.backButton.hidden = step !== 'result';
        if (this.ctaRow) this.ctaRow.classList.toggle('adapter-finder__cta-row--result', step === 'result');
        this.classList.toggle('adapter-finder--result', step === 'result');
      }

      /* ------------------------------------------------------------------ */
      /* Events                                                              */
      /* ------------------------------------------------------------------ */

      bindEvents() {
        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('change', this.onChange.bind(this));
        this.addEventListener('input', this.onInput.bind(this));
      }

      onClick(event) {
        const target = event.target.closest('button');
        if (!target || !this.contains(target)) return;

        if (target.dataset.finderSeriesOption !== undefined) {
          this.state.series = target.dataset.finderSeriesOption;
          if (
            this.state.seats &&
            !this.models.some((m) => m.series === this.state.series && Number(m.seats) === Number(this.state.seats))
          ) {
            this.state.seats = null;
          }
          this.state.model = null;
          this.renderSeries();
          this.renderSeats();
          this.renderModels();
          this.clearError();
          return;
        }

        if (target.dataset.finderSeatsOption !== undefined) {
          this.state.seats = Number(target.dataset.finderSeatsOption);
          this.state.model = null;
          this.renderSeats();
          this.renderModels();
          this.clearError();
          return;
        }

        if (target.hasAttribute('data-finder-model-help-toggle')) {
          this.openModelHelp(target);
          return;
        }

        if (target.hasAttribute('data-finder-model-help-close')) {
          this.closeModelHelp();
          return;
        }

        if (target.hasAttribute('data-finder-search')) {
          this.search();
          return;
        }

        if (target.hasAttribute('data-finder-edit')) {
          this.backToForm();
          return;
        }

        if (target.classList.contains('adapter-finder__guide-link') && this.state.brand) {
          // The guide modal opens via its own document-level listener; just
          // land the shopper on their brand's tab.
          const guide = document.querySelector('adapter-compatibility-guide');
          if (guide && typeof guide.selectBrand === 'function') guide.selectBrand(this.state.brand);
          return;
        }

        if (target.hasAttribute('data-finder-qty-minus')) {
          this.stepQty(-1);
          return;
        }

        if (target.hasAttribute('data-finder-qty-plus')) {
          this.stepQty(1);
        }
      }

      onChange(event) {
        const target = event.target;
        if (target.matches('[data-finder-model]')) {
          this.state.model = target.value || null;
          this.clearError();
          return;
        }
        if (target.matches('[data-finder-brand]')) {
          this.state.brand = target.value || null;
          this.state.seat = null;
          this.renderSeatModels();
          this.clearError();
          return;
        }
        if (target.matches('[data-finder-seat]')) {
          this.state.seat = target.value || null;
          this.clearError();
          return;
        }
        if (target.matches('[data-finder-qty-input]')) {
          this.clampQty();
        }
      }

      onInput(event) {
        if (event.target.matches('[data-finder-qty-input]')) this.clampQty();
      }

      backToForm() {
        this.showStep('form');
        this.lockAtc(this.LOCKED_LABEL, true);
        this.scrollIntoViewIfNeeded();
      }

      /* ------------------------------------------------------------------ */
      /* "How To Find Your Model Number" popup                               */
      /* ------------------------------------------------------------------ */

      openModelHelp(toggle) {
        const modal = this.querySelector('[data-finder-model-help]');
        if (!modal || !modal.hidden) return;
        this.helpToggle = toggle || this.querySelector('[data-finder-model-help-toggle]');
        if (FinderDropdown.open) FinderDropdown.open.close();

        modal.hidden = false;
        document.body.classList.add('adapter-finder-help-open');
        if (this.helpToggle) this.helpToggle.setAttribute('aria-expanded', 'true');

        const dialog = modal.querySelector('[data-finder-model-help-dialog]');
        this.onHelpKeydown = (event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            this.closeModelHelp();
            return;
          }
          // Focus trap (WCAG 2.1.2): keep Tab / Shift+Tab cycling inside the
          // dialog. The dialog itself (tabindex=-1) is where focus starts.
          if (event.key !== 'Tab' || !dialog) return;
          const focusable = Array.from(
            dialog.querySelectorAll(
              'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]',
            ),
          ).filter((el) => el.offsetParent !== null && el.getAttribute('tabindex') !== '-1');
          if (!focusable.length) {
            event.preventDefault();
            dialog.focus({ preventScroll: true });
            return;
          }
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          const active = document.activeElement;
          if (event.shiftKey && (active === first || active === dialog || !dialog.contains(active))) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && (active === last || !dialog.contains(active))) {
            event.preventDefault();
            first.focus();
          }
        };
        this.onHelpOverlayClick = (event) => {
          // The overlay is a <div>, so the delegated onClick (buttons only) never sees it.
          if (event.target.hasAttribute('data-finder-model-help-close')) this.closeModelHelp();
        };
        document.addEventListener('keydown', this.onHelpKeydown);
        modal.addEventListener('click', this.onHelpOverlayClick);

        if (dialog) dialog.focus({ preventScroll: true });
      }

      closeModelHelp() {
        const modal = this.querySelector('[data-finder-model-help]');
        if (!modal || modal.hidden) return;
        modal.hidden = true;
        document.body.classList.remove('adapter-finder-help-open');
        if (this.onHelpKeydown) document.removeEventListener('keydown', this.onHelpKeydown);
        if (this.onHelpOverlayClick) modal.removeEventListener('click', this.onHelpOverlayClick);
        this.onHelpKeydown = null;
        this.onHelpOverlayClick = null;
        if (this.helpToggle) {
          this.helpToggle.setAttribute('aria-expanded', 'false');
          this.helpToggle.focus({ preventScroll: true });
        }
      }

      /**
       * Ignore our own dispatches; if something else (e.g. sticky bar swatch)
       * changes the variant while the finder is in form state, keep the CTA
       * locked so the shopper has to go through Search.
       */
      handleExternalVariantChange() {
        if (this.dispatching) return;
        if (this.classList.contains('adapter-finder--result')) return;
        this.lockAtc(this.LOCKED_LABEL, true);
      }

      /* ------------------------------------------------------------------ */
      /* Search / resolve                                                    */
      /* ------------------------------------------------------------------ */

      search() {
        const missing = [];
        if (!this.state.series) missing.push('series');
        if (!this.state.seats) missing.push('seats');
        if (!this.state.model) missing.push('model');
        if (!this.state.brand) missing.push('car seat brand');
        if (this.state.brand && !this.state.seat) missing.push('car seat');
        if (missing.length) {
          this.showError(missing);
          return;
        }
        this.clearError();

        const model = this.models.find(
          (m) => m.series === this.state.series && Number(m.seats) === Number(this.state.seats) && m.label === this.state.model,
        );
        const brand = this.brandList.find((b) => b.name === this.state.brand);
        const variant = model && model.size && brand ? this.findVariant(model.size, brand.compatValue) : null;

        this.result = { variant, model: model || null, compatible: !!variant };
        this.applyResult();
      }

      /**
       * @param {string} sizeValue
       * @param {string} compatValue
       * @returns {Object|null}
       */
      findVariant(sizeValue, compatValue) {
        const sizeKey = `option${this.config.sizeOptionIndex}`;
        const compatKey = `option${this.config.compatOptionIndex}`;
        const norm = (v) =>
          String(v || '')
            .replace(/\s+/g, ' ')
            .trim()
            .toLowerCase();
        return this.variants.find((v) => norm(v[sizeKey]) === norm(sizeValue) && norm(v[compatKey]) === norm(compatValue)) || null;
      }

      applyResult() {
        this.renderResult();
        this.showStep('result');

        if (!this.result.compatible || !this.result.variant) {
          this.lockAtc(this.INCOMPATIBLE_LABEL);
          this.scrollIntoViewIfNeeded();
          return;
        }

        this.selectVariantRadios(this.result.variant);

        if (this.result.variant.available) {
          this.unlockAtc(this.result.variant.price);
        } else {
          // Out of stock: color-variant-selection has already hidden the ATC
          // and shown the Klaviyo "Notify Me" CTA for this variant. Only lift
          // the form-step lock so that CTA (and Back) are visible.
          this.releaseLock();
        }
        this.scrollIntoViewIfNeeded();
      }

      /**
       * Check the Size + Compatibility radios rendered by
       * component-color-variant-selection and fire a single `change` so its
       * JS resolves the variant and updates price / gallery / URL / hidden id.
       * @param {Object} variant
       */
      selectVariantRadios(variant) {
        const picker = this.form ? this.form.querySelector('color-variant-selection') : document.querySelector('color-variant-selection');
        if (!picker) return;

        const sizeKey = `option${this.config.sizeOptionIndex}`;
        const compatKey = `option${this.config.compatOptionIndex}`;
        const sizeInput = this.findRadio(picker, this.config.sizeOptionIndex, variant[sizeKey]);
        const compatInput = this.findRadio(picker, this.config.compatOptionIndex, variant[compatKey]);

        const inputs = [sizeInput, compatInput].filter(Boolean);
        if (!inputs.length) return;

        this.dispatching = true;
        try {
          for (const input of inputs) input.checked = true;
          // One change on the last input is enough: the picker re-resolves
          // from every checked radio, not just the one that fired.
          inputs[inputs.length - 1].dispatchEvent(new Event('change', { bubbles: true }));
        } finally {
          this.dispatching = false;
        }
      }

      findRadio(picker, optionIndex, value) {
        const fieldset = picker.querySelector(`fieldset[data-option-index="${optionIndex}"]`);
        if (!fieldset) return null;
        const norm = (v) =>
          String(v || '')
            .replace(/\s+/g, ' ')
            .trim()
            .toLowerCase();
        for (const input of fieldset.querySelectorAll('input[type="radio"]')) {
          if (norm(input.dataset.variantTitle) === norm(value)) return input;
        }
        return null;
      }

      /**
       * Deep link support (?variant=): pre-fill the finder from the variant's
       * Size + Compatibility values and jump straight to the result.
       * @param {string} variantId
       * @returns {boolean} true when the result step was shown
       */
      applyPreselectedVariant(variantId) {
        const variant = this.variants.find((v) => String(v.id) === String(variantId));
        if (!variant) return false;

        const sizeValue = variant[`option${this.config.sizeOptionIndex}`];
        const compatValue = variant[`option${this.config.compatOptionIndex}`];
        const model = this.models.find((m) => m.size && m.size.toLowerCase() === String(sizeValue || '').toLowerCase());
        const brand = this.brandList.find((b) => b.compatValue === compatValue);
        if (!model || !brand) return false;

        this.state.series = model.series;
        this.state.seats = Number(model.seats);
        this.state.model = model.label;
        this.state.brand = brand.name;
        this.renderSeries();
        this.renderSeats();
        this.renderModels();
        this.renderBrands();
        this.renderSeatModels();
        const seatSelect = this.querySelector('[data-finder-seat]');
        const firstSeat = seatSelect && seatSelect.options.length > 1 ? seatSelect.options[1].value : null;
        this.state.seat = firstSeat;
        if (seatSelect && firstSeat) seatSelect.value = firstSeat;

        this.result = { variant, model, compatible: true };
        this.applyResult();
        return true;
      }

      /* ------------------------------------------------------------------ */
      /* CTA                                                                 */
      /* ------------------------------------------------------------------ */

      /**
       * Wrap the theme's #add-to-cart-btn so the Back button can sit beside
       * it. The button element itself is only re-parented, never replaced,
       * so observers held by the sticky bar / product-form keep working.
       */
      buildCtaRow() {
        if (!this.atcButton || this.ctaRow) return;
        const row = document.createElement('div');
        row.className = 'adapter-finder__cta-row';
        this.atcButton.parentNode.insertBefore(row, this.atcButton);
        const back = this.querySelector('[data-finder-back]');
        if (back) {
          // Once moved out of <car-seat-adapter-finder> the delegated onClick
          // no longer sees it, so bind directly.
          back.addEventListener('click', this.backToForm.bind(this));
          row.appendChild(back);
          this.backButton = back;
        }
        row.appendChild(this.atcButton);
        this.ctaRow = row;
      }

      /**
       * The red "Please check the Compatibility Guide..." notice renders as a
       * single clamped line (Figma). Tapping the notice expands it; tapping the
       * inline "Compatibility Guide" span still opens the modal (handled by
       * the section's CompatibilityGuide element) without toggling.
       */
      bindNoticeToggle() {
        const notice = this.form ? this.form.querySelector('.compatibility-warning') : null;
        if (!notice) return;
        notice.setAttribute('role', 'button');
        notice.setAttribute('tabindex', '0');
        notice.setAttribute('aria-expanded', 'false');
        const toggle = () => {
          const expanded = notice.classList.toggle('compatibility-warning--expanded');
          notice.setAttribute('aria-expanded', String(expanded));
        };
        notice.addEventListener('click', (event) => {
          if (event.target.closest('span')) return;
          toggle();
        });
        notice.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          toggle();
        });
      }

      /**
       * Disable the ATC with a placeholder label. With `hide` the whole CTA
       * row is hidden (form step: nothing selected yet, so "Select Options"
       * would be noise); without it the disabled label stays visible (result
       * step, e.g. "Not Compatible"). The button stays in the DOM/form either
       * way – product-form.js and the variant scripts query it by id.
       */
      lockAtc(label, hide = false) {
        if (!this.atcButton) return;
        this.atcButton.disabled = true;
        this.atcButton.classList.add('product-atc-btn--locked');
        if (this.ctaRow) this.ctaRow.classList.toggle('adapter-finder__cta-row--hidden', hide);
        // Form-level flag also hides the Klaviyo back-in-stock button (anchor
        // + injected #klaviyo-bis-button-container) while nothing is selected.
        if (this.form) this.form.classList.toggle('adapter-finder-form--locked', hide);
        const span = this.atcButton.querySelector('span');
        if (span) span.textContent = label;
      }

      /**
       * Lift the form-step lock (CTA row + Klaviyo notify hidden) without
       * touching the ATC itself. Used on the result step for out-of-stock
       * variants, where the theme's own OOS handling owns the ATC/notify swap.
       */
      releaseLock() {
        if (this.ctaRow) this.ctaRow.classList.remove('adapter-finder__cta-row--hidden');
        if (this.form) this.form.classList.remove('adapter-finder-form--locked');
      }

      unlockAtc(priceCents) {
        if (!this.atcButton) return;
        this.atcButton.disabled = false;
        this.atcButton.classList.remove('product-atc-btn--locked');
        this.releaseLock();
        this.atcButton.style.display = '';
        const span = this.atcButton.querySelector('span');
        if (span) span.textContent = `${this.ATC_LABEL} - ${this.formatMoney(priceCents)}`;
      }

      formatMoney(cents) {
        if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
          return window.Shopify.formatMoney(cents, (window.theme && window.theme.moneyFormat) || '${{amount}}');
        }
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(cents) / 100);
      }

      /* ------------------------------------------------------------------ */
      /* Quantity                                                            */
      /* ------------------------------------------------------------------ */

      stepQty(delta) {
        const input = this.querySelector('[data-finder-qty-input]');
        if (!input) return;
        input.value = String((Number(input.value) || 1) + delta);
        this.clampQty();
      }

      clampQty() {
        const input = this.querySelector('[data-finder-qty-input]');
        if (!input) return;
        const min = Number(input.min) || 1;
        const max = Number(input.max) || Infinity;
        const value = Math.min(Math.max(Number(input.value) || min, min), max);
        if (String(value) !== input.value) input.value = String(value);
        this.syncQtyButtons();
      }

      syncQtyButtons() {
        const input = this.querySelector('[data-finder-qty-input]');
        const minus = this.querySelector('[data-finder-qty-minus]');
        const plus = this.querySelector('[data-finder-qty-plus]');
        if (!input) return;
        const value = Number(input.value) || 1;
        if (minus) minus.disabled = value <= (Number(input.min) || 1);
        if (plus) plus.disabled = value >= (Number(input.max) || Infinity);
      }

      /* ------------------------------------------------------------------ */
      /* Helpers                                                             */
      /* ------------------------------------------------------------------ */

      /**
       * The error copy ("Can't find your model? Please check the
       * Compatibility Guide") is static markup in the snippet because it
       * carries a link that opens the guide modal, so this only reveals it.
       * The specific missing fields are exposed via data-finder-missing for
       * debugging/analytics.
       * @param {string[]} [missing]
       */
      showError(missing = []) {
        const el = this.querySelector('[data-finder-error]');
        if (!el) return;
        el.dataset.finderMissing = missing.join(',');
        el.hidden = false;
      }

      clearError() {
        const el = this.querySelector('[data-finder-error]');
        if (!el) return;
        delete el.dataset.finderMissing;
        el.hidden = true;
      }

      scrollIntoViewIfNeeded() {
        const rect = this.getBoundingClientRect();
        if (rect.top >= 0 && rect.top < window.innerHeight * 0.6) return;
        this.scrollToFinder();
      }

      /** Scroll so the finder's top sits just below the fixed header. */
      scrollToFinder() {
        const header = document.querySelector('.navigation-wrapper');
        const offset = (header ? header.offsetHeight : 0) + 16;
        window.scrollTo({ top: this.getBoundingClientRect().top + window.scrollY - offset, behavior: 'smooth' });
      }
    },
  );
}
